/* eslint-disable @typescript-eslint/no-explicit-any -- Legacy operations team settings payloads still normalize highly dynamic JSON structures. */
import { normalizeTemplateOrderKey } from '@/lib/operations/solutionTemplateOrder';
import {
  getEffectiveOperationsActionPermission,
  mergeSettingsWithOperationsActionPermissions,
} from '@/lib/operations/actionPermissions';
import { normalizeSolutionItemTypes } from '@/lib/operations/itemTypes';

export type OperationsTeamRole = 'user' | 'supervisor' | 'admin';

export interface OperationsTeamRoleDefinition {
  key: string;
  label: string;
  description?: string;
  base_role: OperationsTeamRole;
  system?: boolean;
  locked?: boolean;
}

const DEFAULT_OPERATIONS_TEAM_ROLE_DEFINITIONS: OperationsTeamRoleDefinition[] = [
  {
    key: 'user',
    label: 'User',
    description: 'Standard operational team member',
    base_role: 'user',
    system: true,
  },
  {
    key: 'supervisor',
    label: 'Supervisor',
    description: 'Can oversee and coordinate team activity',
    base_role: 'supervisor',
    system: true,
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Full team administration rights',
    base_role: 'admin',
    system: true,
    locked: true,
  },
];

const ROLE_ALIAS_MAP: Record<string, OperationsTeamRole> = {
  member: 'user',
  user: 'user',
  lead: 'supervisor',
  supervisor: 'supervisor',
  admin: 'admin',
  owner: 'admin',
  system_admin: 'admin',
};

function normalizeOperationsTeamRoleKeyValue(role: unknown): string {
  return typeof role === 'string'
    ? role.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    : '';
}

function toTitleCaseRoleLabel(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

type OperationsTeamRoleSource =
  | { settings?: Record<string, any> | null }
  | Record<string, any>
  | Array<OperationsTeamRoleDefinition>
  | null
  | undefined;

function getSettingsFromRoleSource(source: OperationsTeamRoleSource): Record<string, any> | null {
  if (!source) {
    return null;
  }

  if (Array.isArray(source)) {
    return { role_definitions: source };
  }

  if (typeof source !== 'object') {
    return null;
  }

  if ('settings' in source && source.settings && typeof source.settings === 'object' && !Array.isArray(source.settings)) {
    return source.settings as Record<string, any>;
  }

  return source as Record<string, any>;
}

function normalizeCanonicalOperationsTeamRole(role: unknown): OperationsTeamRole {
  if (typeof role !== 'string') return 'user';
  return ROLE_ALIAS_MAP[normalizeOperationsTeamRoleKeyValue(role)] || 'user';
}

export function getDefaultOperationsTeamRoleDefinitions(): OperationsTeamRoleDefinition[] {
  return DEFAULT_OPERATIONS_TEAM_ROLE_DEFINITIONS.map((definition) => ({ ...definition }));
}

export function getOperationsTeamRoleDefinitions(source?: OperationsTeamRoleSource): OperationsTeamRoleDefinition[] {
  const settings = getSettingsFromRoleSource(source);
  const rawRoleDefinitions = Array.isArray(settings?.role_definitions)
    ? settings.role_definitions
    : [];

  const definitionMap = new Map<string, OperationsTeamRoleDefinition>(
    DEFAULT_OPERATIONS_TEAM_ROLE_DEFINITIONS.map((definition) => [definition.key, { ...definition }])
  );

  rawRoleDefinitions.forEach((rawDefinition) => {
    if (!rawDefinition || typeof rawDefinition !== 'object' || Array.isArray(rawDefinition)) {
      return;
    }

    const key = normalizeOperationsTeamRoleKeyValue(
      (rawDefinition as Record<string, any>).key || (rawDefinition as Record<string, any>).label
    );

    if (!key) {
      return;
    }

    const label = typeof (rawDefinition as Record<string, any>).label === 'string' && (rawDefinition as Record<string, any>).label.trim().length > 0
      ? (rawDefinition as Record<string, any>).label.trim()
      : toTitleCaseRoleLabel(key);
    const description = typeof (rawDefinition as Record<string, any>).description === 'string'
      ? (rawDefinition as Record<string, any>).description.trim()
      : '';
    const baseRole = normalizeCanonicalOperationsTeamRole((rawDefinition as Record<string, any>).base_role || key);

    if (key === 'admin') {
      definitionMap.set('admin', {
        ...definitionMap.get('admin')!,
        label: 'Admin',
        description: description || definitionMap.get('admin')?.description,
        base_role: 'admin',
        system: true,
        locked: true,
      });
      return;
    }

    if (key === 'user' || key === 'supervisor') {
      definitionMap.set(key, {
        ...definitionMap.get(key)!,
        label,
        description: description || definitionMap.get(key)?.description,
        base_role: key as OperationsTeamRole,
        system: true,
      });
      return;
    }

    definitionMap.set(key, {
      key,
      label,
      description,
      base_role: baseRole,
      system: false,
      locked: false,
    });
  });

  const definitions = Array.from(definitionMap.values());
  const systemOrder = ['user', 'supervisor', 'admin'];

  return definitions.sort((a, b) => {
    const aSystemIndex = systemOrder.indexOf(a.key);
    const bSystemIndex = systemOrder.indexOf(b.key);

    if (aSystemIndex !== -1 || bSystemIndex !== -1) {
      if (aSystemIndex === -1) return 1;
      if (bSystemIndex === -1) return -1;
      return aSystemIndex - bSystemIndex;
    }

    return a.label.localeCompare(b.label);
  });
}

export function normalizeOperationsTeamRoleKey(role: unknown, source?: OperationsTeamRoleSource): string {
  const normalizedKey = normalizeOperationsTeamRoleKeyValue(role);
  const roleDefinitions = getOperationsTeamRoleDefinitions(source);
  const matchingDefinition = roleDefinitions.find((definition) => definition.key === normalizedKey);

  if (matchingDefinition) {
    return matchingDefinition.key;
  }

  return normalizeCanonicalOperationsTeamRole(role);
}

export function getOperationsTeamRoleDefinition(
  role: unknown,
  source?: OperationsTeamRoleSource,
): OperationsTeamRoleDefinition {
  const normalizedKey = normalizeOperationsTeamRoleKeyValue(role);
  const roleDefinitions = getOperationsTeamRoleDefinitions(source);
  const matchingDefinition = roleDefinitions.find((definition) => definition.key === normalizedKey);

  if (matchingDefinition) {
    return matchingDefinition;
  }

  const canonicalRole = normalizeCanonicalOperationsTeamRole(role);
  return roleDefinitions.find((definition) => definition.key === canonicalRole)
    || getDefaultOperationsTeamRoleDefinitions().find((definition) => definition.key === canonicalRole)
    || getDefaultOperationsTeamRoleDefinitions()[0];
}

export function normalizeOperationsTeamRole(role: unknown, source?: OperationsTeamRoleSource): OperationsTeamRole {
  return getOperationsTeamRoleDefinition(role, source).base_role;
}

export function getOperationsTeamRoleLabel(role: unknown, source?: OperationsTeamRoleSource): string {
  return getOperationsTeamRoleDefinition(role, source).label;
}

export function parseSmartTaskTemplateKeys(value: unknown): string[] {
  const rawKeys = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim().length > 0
      ? [value]
      : [];

  const seen = new Set<string>();
  const templateKeys: string[] = [];

  rawKeys.forEach((rawKey) => {
    const normalizedKey = normalizeTemplateOrderKey(rawKey);
    if (!normalizedKey || seen.has(normalizedKey)) {
      return;
    }

    seen.add(normalizedKey);
    templateKeys.push(normalizedKey);
  });

  return templateKeys;
}

export function buildSmartTaskTemplateAssignment(value: unknown): {
  smart_task_template_keys: string[];
  smart_task_template_key: string | null;
} {
  const smart_task_template_keys = parseSmartTaskTemplateKeys(value);
  return {
    smart_task_template_keys,
    smart_task_template_key: smart_task_template_keys[0] || null,
  };
}

interface OperationsTeamSettingsSource {
  role?: unknown;
  item_types?: unknown;
  itemTypes?: unknown;
  settings?: Record<string, any> | null;
  featurePolicies?: Record<string, any> | null;
  featurePoliciesByRoleScope?: Record<string, Record<string, any>> | null;
  featureOverrides?: Record<string, any> | null;
}

const ACTION_MANAGED_SMART_TASK_FEATURE_BY_ITEM_TYPE: Record<string, string> = {
  assessment: 'smart_task_assessment_recording',
  breakdown: 'smart_task_breakdown_recording',
  credit_note: 'smart_task_credit_note_creation',
  fuel: 'smart_task_fuel_recording',
  maintenance: 'smart_task_maintenance_recording',
  proposal: 'smart_task_proposal_creation',
  purchase: 'smart_task_purchase_recording',
  route_stop: 'smart_task_route_stop_recording',
  site_visit: 'smart_task_site_visit_recording',
  supervision: 'smart_task_supervision_recording',
  toll: 'smart_task_toll_recording',
  training: 'smart_task_training_recording',
  // expense, invoice, quote migrated to operations_action_permissions (Actions tab SSOT).
};

const ACTION_MANAGED_CHILD_ENTRY_ITEM_TYPES = new Set([
  'assessment',
  'maintenance',
  'route_stop',
  'site_visit',
  'supervision',
  'training',
]);

const ADMIN_DEFAULT_ALLOW_SETTINGS: Record<string, true> = {
  operations_job_billing_timers_signoff: true,
  operations_job_edit_timers_for_completion: true,
  operations_job_edit_work_item_comments: true,
};

function syncActionManagedSmartTaskSettings(
  settings: Record<string, any>,
  role: OperationsTeamRole,
  rawItemTypes: unknown,
): Record<string, any> {
  const normalizedItemTypes = normalizeSolutionItemTypes(rawItemTypes);
  if (normalizedItemTypes.length === 0) {
    return settings;
  }

  const nextSettings: Record<string, any> = { ...settings };

  normalizedItemTypes.forEach((itemType) => {
    const featureKey = ACTION_MANAGED_SMART_TASK_FEATURE_BY_ITEM_TYPE[itemType];
    if (!featureKey) {
      return;
    }

    nextSettings[featureKey] = getEffectiveOperationsActionPermission(nextSettings, role, itemType, 'create');
  });

  if (normalizedItemTypes.some((itemType) => ACTION_MANAGED_CHILD_ENTRY_ITEM_TYPES.has(itemType))) {
    nextSettings.smart_task_child_entries = normalizedItemTypes.some((itemType) => (
      ACTION_MANAGED_CHILD_ENTRY_ITEM_TYPES.has(itemType)
      && getEffectiveOperationsActionPermission(nextSettings, role, itemType, 'create')
    ));
  }

  return nextSettings;
}

export function hasEnabledSmartTaskCapability(settings: Record<string, any> | null | undefined): boolean {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return false;
  }

  if (Boolean(settings.smart_task_field_worker || settings.smart_task)) {
    return true;
  }

  return Object.entries(settings).some(([key, value]) => (
    key.startsWith('smart_task_')
    && key !== 'smart_task_field_worker'
    && key !== 'smart_task_mobile_only'
    && typeof value === 'boolean'
    && value
  ));
}

function toPlainObject(value: unknown): Record<string, any> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, any>;
}

function normalizeMeaningfulOverrides(value: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(value).filter(([, overrideValue]) => {
      if (overrideValue === undefined || overrideValue === null) return false;
      if (Array.isArray(overrideValue)) return overrideValue.length > 0;
      if (typeof overrideValue === 'string') return overrideValue.trim().length > 0;
      return true;
    })
  );
}

function getTemplateAssignmentFromSettings(settings: Record<string, any>): {
  smart_task_template_keys: string[];
  smart_task_template_key: string | null;
} | null {
  const hasTemplateKeys = Object.prototype.hasOwnProperty.call(settings, 'smart_task_template_keys');
  const hasTemplateKey = Object.prototype.hasOwnProperty.call(settings, 'smart_task_template_key');

  if (!hasTemplateKeys && !hasTemplateKey) {
    return null;
  }

  const fromKeys = parseSmartTaskTemplateKeys(settings.smart_task_template_keys);
  if (fromKeys.length > 0) {
    return buildSmartTaskTemplateAssignment(fromKeys);
  }

  if (typeof settings.smart_task_template_key === 'string' && settings.smart_task_template_key.trim().length > 0) {
    return buildSmartTaskTemplateAssignment(settings.smart_task_template_key.trim());
  }

  return {
    smart_task_template_keys: [],
    smart_task_template_key: null,
  };
}

export function getOperationsTeamSmartTaskSettings(
  team: OperationsTeamSettingsSource | null | undefined
): Record<string, any> {
  const normalizedRole = normalizeOperationsTeamRole(team?.role, team?.settings);
  const normalizedRoleKey = normalizeOperationsTeamRoleKey(team?.role, team?.settings);
  const settings = toPlainObject(team?.settings);
  const allRoleDefaults = toPlainObject(settings.role_defaults);
  const baseRoleDefaults = toPlainObject(allRoleDefaults[normalizedRole]);
  const specificRoleDefaults = toPlainObject(allRoleDefaults[normalizedRoleKey]);
  const implicitRoleDefaults = normalizedRole === 'admin'
    ? ADMIN_DEFAULT_ALLOW_SETTINGS
    : {};
  const roleDefaults = mergeSettingsWithOperationsActionPermissions(
    implicitRoleDefaults,
    baseRoleDefaults,
    specificRoleDefaults,
  );
  const featurePolicies = toPlainObject(team?.featurePolicies);
  const featurePoliciesByRoleScope = toPlainObject(team?.featurePoliciesByRoleScope);
  const rawFeatureOverrides = toPlainObject(team?.featureOverrides);
  const featureOverrides = normalizeMeaningfulOverrides(rawFeatureOverrides);

  // Role-scoped group policies (role_scope matching the member's normalized
  // role or role key) apply on top of role defaults — mirroring the two-pass
  // merge in resolveEffectivePermissions where role-scoped group policies
  // override general policies and role defaults. Both lookup keys are merged
  // because role_scope values may be written as either a base role ('admin')
  // or a role key ('admin:field-services').
  const scopedFeaturePolicies = mergeSettingsWithOperationsActionPermissions(
    toPlainObject(featurePoliciesByRoleScope[normalizedRole]),
    toPlainObject(featurePoliciesByRoleScope[normalizedRoleKey]),
  );

  const mergedSettings = mergeSettingsWithOperationsActionPermissions(
    featurePolicies,
    roleDefaults,
    scopedFeaturePolicies,
    featureOverrides,
  );

  const resolvedTemplateAssignment = getTemplateAssignmentFromSettings(rawFeatureOverrides)
    || getTemplateAssignmentFromSettings(scopedFeaturePolicies)
    || getTemplateAssignmentFromSettings(roleDefaults)
    || getTemplateAssignmentFromSettings(featurePolicies);

  if (resolvedTemplateAssignment) {
    mergedSettings.smart_task_template_keys = resolvedTemplateAssignment.smart_task_template_keys;
    mergedSettings.smart_task_template_key = resolvedTemplateAssignment.smart_task_template_key;
  }

  return syncActionManagedSmartTaskSettings(
    mergedSettings,
    normalizedRole,
    team?.itemTypes ?? team?.item_types,
  );
}

export function isSmartTaskEnabledForTeam(team: OperationsTeamSettingsSource | null | undefined): boolean {
  const settings = getOperationsTeamSmartTaskSettings(team);
  return hasEnabledSmartTaskCapability(settings);
}
export function isFieldWorkerEnabledForTeam(team: OperationsTeamSettingsSource | null | undefined): boolean {
  const settings = getOperationsTeamSmartTaskSettings(team);

  if (Object.prototype.hasOwnProperty.call(settings, 'is_field_worker')) {
    return Boolean(settings.is_field_worker);
  }

  return Boolean(settings.smart_task_field_worker || settings.smart_task);
}

export function hasCustomSettings(overrides: unknown): boolean {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return false;
  }

  const overrideEntries = overrides as Record<string, unknown>;

  if (
    Object.prototype.hasOwnProperty.call(overrideEntries, 'smart_task_template_keys')
    || Object.prototype.hasOwnProperty.call(overrideEntries, 'smart_task_template_key')
  ) {
    return true;
  }

  return Object.entries(overrideEntries).some(([, value]) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  });
}
